import React, { useEffect, useRef, useState } from 'react';
import {
  BILL_CATEGORIES,
  DEFAULT_DUE_SOON_DAYS,
  DUE_SOON_DAYS,
  Expense,
  remindAtOf,
} from '../data/expenses';
import { PALETTE, PALETTE_HEX } from '../data/palette';

/** Legacy stored colors were a PALETTE index (number); normalize to a hex string. */
function normalizeColor(c: unknown): string | undefined {
  if (typeof c === 'string') return c;
  if (typeof c === 'number') return PALETTE[c]?.bg;
  return undefined;
}

/** '' → undefined; anything else → non-negative number, or undefined if not parseable. */
function parseAmount(raw: string): number | undefined {
  const t = raw.trim();
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/** Pretty display for the date field ("13 Aug 2026"); '' → '' */
function formatDateDisplay(iso: string): string {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-HK', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Quick-pick app links. HSBC HK exposes no public deep link or universal link,
 * so its chip uses an **iOS Shortcut** (`shortcuts://run-shortcut?name=OpenHSBC`)
 * — the user creates a one-time "OpenHSBC" shortcut (Shortcuts app → Open App →
 * HSBC HK Mobile Banking) and tapping the note runs it, launching the app.
 * Amex's chip points at the official app page (no public deep link either).
 */
const APP_LINK_PRESETS: { label: string; url: string; hint: string }[] = [
  {
    label: 'HSBC HK',
    url: 'shortcuts://run-shortcut?name=OpenHSBC',
    hint: 'Runs the "OpenHSBC" iOS Shortcut to launch the app (create it once in the Shortcuts app)',
  },
  {
    label: 'Amex HK',
    url: 'https://www.americanexpress.com/hk/en/customer-service/digital/amex-app.html',
    hint: 'Official page — no public app deep link; overwrite with a scheme you have tested',
  },
];

interface NoteEditorProps {
  /** The note being edited, or null for a brand-new note */
  item: Expense | null;
  isNew: boolean;
  onSave: (item: Expense) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onClose: () => void;
}

const NoteEditor: React.FC<NoteEditorProps> = ({
  item,
  isNew,
  onSave,
  onDelete,
  onDuplicate,
  onClose,
}) => {
  const [title, setTitle] = useState(item?.title ?? '');
  const [date, setDate] = useState(item?.date ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [link, setLink] = useState(item?.link ?? '');
  const [linkType, setLinkType] = useState<'web' | 'app'>(item?.linkType ?? 'web');
  const [amount, setAmount] = useState(item?.amount != null ? String(item.amount) : '');
  const [remindAt, setRemindAt] = useState(remindAtOf(item));
  /** Category tag (v0.14); undefined = none. */
  const [category, setCategory] = useState<string | undefined>(item?.category);
  /** Heads-up lead in days before the due date (v0.14); defaults to 3. */
  const [dueSoonDays, setDueSoonDays] = useState<number>(
    item?.dueSoonDays ?? DEFAULT_DUE_SOON_DAYS
  );
  /** undefined = auto color (hash-based), otherwise a hex color string */
  const [color, setColor] = useState<string | undefined>(normalizeColor(item?.color));

  /** Bottom-sheet gesture: how far the sheet has been pulled down (px) */
  const [dragY, setDragY] = useState(0);
  /** True while the sheet animates off-screen before onClose fires */
  const [closing, setClosing] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Hold the sheet (anywhere except form controls/buttons) and pull down:
  // past ~30% of its height it slides away and closes, otherwise it snaps back.
  // Gestures that start inside the scrollable content when it's scrolled down
  // are left to native scrolling.
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;

    let startY: number | null = null;
    let dragged = 0;

    const onTouchStart = (e: TouchEvent) => {
      const target = e.target as Element;
      if (closing || el.scrollTop > 0 || target.closest('button, input, textarea, select, a')) return;
      startY = e.touches[0].clientY;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (startY === null) return;
      const dy = e.touches[0].clientY - startY;
      if (dy > 0) {
        e.preventDefault(); // stop iOS rubber-band scrolling so the sheet follows the finger
        dragged = dy;
        setDragY(dy);
      }
    };
    const onTouchEnd = () => {
      if (startY === null) return;
      startY = null;
      const threshold = Math.max(el.offsetHeight * 0.3, 120);
      if (dragged > threshold) {
        setClosing(true);
        window.setTimeout(onClose, 300);
      } else {
        dragged = 0;
        setDragY(0);
      }
    };
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Element;
      if (closing || el.scrollTop > 0 || target.closest('button, input, textarea, select, a')) return;
      startY = e.clientY;
    };
    const onMouseMove = (e: MouseEvent) => {
      if (startY === null) return;
      const dy = e.clientY - startY;
      if (dy > 0) {
        dragged = dy;
        setDragY(dy);
      }
    };
    const onMouseUp = onTouchEnd;

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [onClose, closing]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      id: item?.id ?? `new-${Date.now()}`,
      title: title.trim() || 'Untitled bill',
      date,
      description: description.trim(),
      link: link.trim(),
      linkType,
      amount: parseAmount(amount),
      remindAt,
      category,
      dueSoonDays: dueSoonDays === DEFAULT_DUE_SOON_DAYS ? undefined : dueSoonDays,
      completed: item?.completed ?? false,
      color,
    });
  };

  const isCustomColor = color !== undefined && !PALETTE_HEX.includes(color);
  const customHex = isCustomColor ? color : '#6c5ce7';

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        ref={modalRef}
        className={`modal ${dragY > 0 && !closing ? 'modal--dragging' : ''} ${closing ? 'modal--closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={isNew ? 'New bill' : 'Edit bill'}
        onClick={(e) => e.stopPropagation()}
        style={
          dragY > 0 || closing
            ? { transform: closing ? 'translateY(110%)' : `translateY(${dragY}px)` }
            : undefined
        }
      >
        <div className="modal-grabber" aria-hidden="true" />
        <h2 className="modal-title">{isNew ? 'New bill' : 'Edit bill'}</h2>

        <form onSubmit={handleSubmit}>
          <label className="field">
            <span className="field-label">Title</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Electricity"
              autoFocus={isNew}
            />
          </label>

          <label className="field">
            <span className="field-label">Due date</span>
            <div className="native-input-wrap">
              <span
                className={`native-input-display ${date ? '' : 'native-input-display--empty'}`}
                aria-hidden="true"
              >
                {formatDateDisplay(date) || 'Select a date'}
              </span>
              {/* The real native date input sits invisible on top: it opens the
                  iOS wheel when tapped, while the visible layer above is a plain
                  span that can never push past the phone's edge (v0.12.2). */}
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="native-input-overlay"
                aria-label="Due date"
                tabIndex={0}
              />
            </div>
          </label>

          <div className="field">
            <label className="field-label" htmlFor="remind-time">
              Reminder time
            </label>
            <div className="native-input-wrap">
              <span className="native-input-display" aria-hidden="true">
                {remindAt}
              </span>
              <input
                id="remind-time"
                type="time"
                value={remindAt}
                onChange={(e) => setRemindAt(e.target.value)}
                className="native-input-overlay"
                tabIndex={0}
              />
            </div>
            <span className="field-hint">
              Push time for the due-today and heads-up reminders (default 08:00) — the 9pm nightly summary always fires at 21:00.
            </span>
          </div>

          <div className="field">
            <span className="field-label" id="heads-up-label">
              Heads-up before due
            </span>
            <div
              className="chip-row"
              role="radiogroup"
              aria-labelledby="heads-up-label"
            >
              {DUE_SOON_DAYS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`chip ${dueSoonDays === d ? 'chip--active' : ''}`}
                  onClick={() => setDueSoonDays(d)}
                  role="radio"
                  aria-checked={dueSoonDays === d}
                >
                  {d} {d === 1 ? 'day' : 'days'}
                </button>
              ))}
            </div>
            <span className="field-hint">
              Remind me this many days before the due date (default 3 days).
            </span>
          </div>

          <label className="field">
            <span className="field-label">Amount (HK$)</span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 8500"
              min="0"
              step="0.01"
              inputMode="decimal"
            />
          </label>

          <div className="field">
            <span className="field-label" id="category-label">
              Category
            </span>
            <div className="chip-row" role="radiogroup" aria-labelledby="category-label">
              <button
                type="button"
                className={`chip ${category === undefined ? 'chip--active' : ''}`}
                onClick={() => setCategory(undefined)}
                role="radio"
                aria-checked={category === undefined}
              >
                None
              </button>
              {BILL_CATEGORIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`chip ${category === c ? 'chip--active' : ''}`}
                  onClick={() => setCategory(c)}
                  role="radio"
                  aria-checked={category === c}
                >
                  {c}
                </button>
              ))}
            </div>
            <span className="field-hint">Groups bills with subtotals in the totals view.</span>
          </div>

          <label className="field">
            <span className="field-label">Description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. CLP Power bill for August"
              rows={6}
            />
          </label>

          <div className="field">
            <span className="field-label">Open target</span>
            <div className="link-type-row" role="radiogroup" aria-label="Open target">
              <button
                type="button"
                className={`link-type-btn ${linkType === 'web' ? 'link-type-btn--active' : ''}`}
                onClick={() => setLinkType('web')}
                role="radio"
                aria-checked={linkType === 'web'}
              >
                Website
              </button>
              <button
                type="button"
                className={`link-type-btn ${linkType === 'app' ? 'link-type-btn--active' : ''}`}
                onClick={() => setLinkType('app')}
                role="radio"
                aria-checked={linkType === 'app'}
              >
                App
              </button>
            </div>
          </div>

          {linkType === 'web' ? (
            <label className="field">
              <span className="field-label">Website link (optional)</span>
              <input
                type="url"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://..."
                inputMode="url"
              />
            </label>
          ) : (
            <div className="field">
              <span className="field-label">App link (optional)</span>
              <div className="app-chips">
                {APP_LINK_PRESETS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    className={`app-chip ${link === p.url ? 'app-chip--active' : ''}`}
                    onClick={() => setLink(p.url)}
                    title={p.hint}
                    aria-label={`Use ${p.label} link`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="Paste an app link or scheme, e.g. hsbc-hk://"
                inputMode="url"
                aria-label="App link (optional)"
              />
            </div>
          )}

          <div className="field">
            <span className="field-label">Note color</span>
            <div className="color-row" role="radiogroup" aria-label="Note color">
              <button
                type="button"
                className={`color-swatch color-swatch--auto ${color === undefined ? 'color-swatch--active' : ''}`}
                onClick={() => setColor(undefined)}
                role="radio"
                aria-checked={color === undefined}
                aria-label="Auto color"
                title="Auto color"
              >
                <span className="color-auto-icon" aria-hidden="true">
                  ✦
                </span>
              </button>
              {PALETTE.map((c, i) => (
                <button
                  key={i}
                  type="button"
                  className={`color-swatch ${color === c.bg ? 'color-swatch--active' : ''}`}
                  style={{ background: c.bg, borderColor: c.edge }}
                  onClick={() => setColor(c.bg)}
                  role="radio"
                  aria-checked={color === c.bg}
                  aria-label={`Color ${i + 1}`}
                  title={`Color ${i + 1}`}
                />
              ))}
              <label
                className={`color-swatch color-swatch--custom ${isCustomColor ? 'color-swatch--active' : ''}`}
                style={isCustomColor ? { background: color } : undefined}
                role="radio"
                aria-checked={isCustomColor}
                aria-label="Custom color"
                title="Custom color (color plate)"
              >
                <input
                  type="color"
                  value={customHex}
                  onChange={(e) => setColor(e.target.value)}
                  aria-label="Pick a custom color"
                />
                <span className="color-custom-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none">
                    <path
                      d="M12 3s6.5 6.9 6.5 11.2a6.5 6.5 0 1 1-13 0C5.5 9.9 12 3 12 3z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
              </label>
            </div>
          </div>

          <div className="modal-actions">
            {!isNew && (
              <>
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => onDelete(item!.id)}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => onDuplicate(item!.id)}
                >
                  Duplicate
                </button>
              </>
            )}
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NoteEditor;
